import React, { useState } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import {
  ChartCard, ChartToggle, GraficoTooltip, LegendaPills, AXIS_STYLE, GRID_PROPS, fmt,
} from '../../../components/orizon-ui/Grafici';

/**
 * Distribuzione degli interventi per una categoria (gru, reparto, causa...), con barre o torta.
 * dati: [{ chiave, label, valore, colore }] già ordinati.
 */
const GraficoDistribuzione = ({ titolo, sottotitolo, icona, contextLabel, dati, totale }) => {
  const [tipo, setTipo] = useState('bar'); // bar | pie

  if (dati.length === 0) return null;

  return (
    <ChartCard
      title={titolo}
      subtitle={sottotitolo}
      icon={icona}
      contextLabel={contextLabel}
      actions={(
        <ChartToggle
          value={tipo}
          onChange={setTipo}
          options={[
            { value: 'bar', icon: 'BarChart2', label: 'Bar' },
            { value: 'pie', icon: 'PieChart', label: 'Pie' },
          ]}
        />
      )}
    >
      {tipo === 'pie' ? (
        <>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={dati}
                dataKey="valore"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={88}
                paddingAngle={3}
                strokeWidth={0}
              >
                {dati.map((d) => <Cell key={d.chiave} fill={d.colore} />)}
              </Pie>
              <Tooltip content={<GraficoTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          <div className="flex flex-col gap-1.5 pt-2 border-t border-border/10">
            {dati.map((d) => {
              const pct = totale > 0 ? ((d.valore / totale) * 100).toFixed(1) : 0;
              return (
                <div key={d.chiave} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ background: d.colore }} />
                    <span className="text-[10px] font-bold text-foreground/70 truncate">{d.label}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="w-24 h-1.5 rounded-full bg-muted/30 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: d.colore }} />
                    </div>
                    <span className="text-[10px] font-black tabular-nums" style={{ color: d.colore }}>{fmt(d.valore)}</span>
                    <span className="text-[9px] text-muted-foreground/40 font-bold w-10 text-right">{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dati} margin={{ top: 5, right: 5, left: -18, bottom: 0 }} barCategoryGap="35%">
              <CartesianGrid {...GRID_PROPS} />
              <XAxis dataKey="label" tick={AXIS_STYLE} tickLine={false} axisLine={false} interval={0} tickFormatter={(v) => (String(v).length > 12 ? `${String(v).slice(0, 11)}…` : v)} />
              <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
              <Tooltip content={<GraficoTooltip />} cursor={{ fill: 'rgba(128,128,128,0.05)' }} />
              <Bar dataKey="valore" name="Interventi" radius={[8, 8, 0, 0]} maxBarSize={55}>
                {dati.map((d) => <Cell key={d.chiave} fill={d.colore} />)}
                <LabelList dataKey="valore" position="top" style={{ fontSize: 10, fontWeight: 900, fill: 'rgba(128,128,128,0.6)' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <LegendaPills voci={dati.map((d) => ({ chiave: d.chiave, label: d.label, colore: d.colore, totale: d.valore }))} />
        </>
      )}
    </ChartCard>
  );
};

export default GraficoDistribuzione;
